import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, Reorder, useDragControls } from "framer-motion";
import {
  Plus, Sparkles, Gavel, CheckCircle2, Circle, Loader2, ListChecks, Clock,
  User, Mail, Search, MoreHorizontal, Paperclip, ChevronRight, Pencil,
  Trash2, GripVertical, Upload, FileText, Download, Copy,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, openSigned, API } from "@/lib/api";
import { StatusBadge } from "@/components/badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { formatDateTime } from "@/lib/format";
import { GfVoting } from "@/pages/company/GfVoting";
import { LivePoll } from "./LivePoll";
import { initials, durLabel, FAST_SAKER } from "./utils";

const ASSIGNABLE_ROLES = ["Styreleder", "Nestleder", "Styremedlem", "Varamedlem", "Daglig leder"];

function ReorderCard({ a, idx, selected, onClick, onDragEnd }) {
  const controls = useDragControls();
  const Icon = a.decision ? CheckCircle2 : Circle;
  return (
    <Reorder.Item
      value={a}
      dragListener={false}
      dragControls={controls}
      onDragEnd={onDragEnd}
      className={`list-none rounded-xl border bg-card transition-colors hover:bg-muted/50 ${
        selected ? "border-l-4 border-l-primary border-border ring-1 ring-primary/20" : "border-border"
      }`}
    >
      <div className="flex items-stretch gap-1">
        <button
          type="button"
          onPointerDown={(e) => controls.start(e)}
          className="flex shrink-0 cursor-grab touch-none items-center rounded-l-xl px-1.5 text-muted-foreground/40 transition-colors hover:text-muted-foreground active:cursor-grabbing"
          title="Dra for å endre rekkefølge"
          aria-label="Endre rekkefølge"
          data-testid={`agenda-drag-${a.id}`}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <button data-testid={`agenda-card-${a.id}`} onClick={onClick} className="min-w-0 flex-1 py-3.5 pr-3.5 text-left">
          <div className="flex items-start gap-2">
            <span className="mt-0.5 text-xs font-bold text-muted-foreground">{idx + 1}.</span>
            <p className="flex-1 text-sm font-semibold leading-snug">{a.title}</p>
          </div>
          <div className="mt-2.5 flex items-center gap-3 border-t border-border/60 pt-2 text-xs text-muted-foreground">
            <Icon className={`h-3.5 w-3.5 ${a.decision ? "text-emerald-500" : "text-muted-foreground/50"}`} />
            {a.responsible ? (
              <span className="flex min-w-0 items-center gap-1.5">
                <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-primary/10 text-[9px] font-semibold text-primary">{initials(a.responsible)}</span>
                <span className="truncate">{a.responsible}</span>
              </span>
            ) : null}
            {a.duration_min ? <span className="ml-auto flex items-center gap-1"><Clock className="h-3 w-3" /> {a.duration_min}</span> : null}
          </div>
        </button>
      </div>
    </Reorder.Item>
  );
}

export function AgendaTab({ m, meetingId, companyId, isGf, gf, load, companyTasks, loadTasks, live, refreshLive }) {
  const [selectedId, setSelectedId] = useState(null);
  const [caseMode, setCaseMode] = useState("innkalling");
  const [agendaOpen, setAgendaOpen] = useState(false);
  const [agenda, setAgenda] = useState({ title: "", description: "", category: "Sak", proposal: "", duration_min: "", responsible: "" });
  const [drafting, setDrafting] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [suggLoading, setSuggLoading] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [sheetSearch, setSheetSearch] = useState("");
  const [decisionFor, setDecisionFor] = useState(null);
  const [decisionForm, setDecisionForm] = useState({ decision: "", create_task: true, task_title: "", assignee_name: "", due_date: "" });
  const [savingDecision, setSavingDecision] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ id: null, title: "", description: "", category: "Sak", proposal: "", duration_min: "", responsible: "" });
  const [editDrafting, setEditDrafting] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);

  // Ansvarlig / oppgave-mottaker kan velges blant alle styremedlemmer (ikke bare
  // lagrede møtedeltakere), slik at f.eks. styreleder alltid er valgbar.
  const [members, setMembers] = useState([]);
  useEffect(() => {
    api.get(`/companies/${companyId}/members`).then((r) => setMembers(r.data || [])).catch(() => setMembers([]));
  }, [companyId]);
  const assignable = useMemo(() => {
    const byName = new Map();
    (m.attendees || []).forEach((a) => { if (a.name && !byName.has(a.name)) byName.set(a.name, { name: a.name, role: a.role, user_id: a.user_id }); });
    (members || []).filter((mm) => ASSIGNABLE_ROLES.includes(mm.role)).forEach((mm) => { if (mm.name && !byName.has(mm.name)) byName.set(mm.name, { name: mm.name, role: mm.role, user_id: mm.user_id }); });
    return Array.from(byName.values());
  }, [m.attendees, members]);

  const openEdit = (a) => {
    setEditForm({
      id: a.id,
      title: a.title || "",
      description: a.description || "",
      category: a.category || "Sak",
      proposal: a.proposal || "",
      duration_min: a.duration_min != null ? String(a.duration_min) : "",
      responsible: a.responsible || "",
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editForm.title.trim()) { toast.error("Sakstittel kan ikke være tom"); return; }
    setSavingEdit(true);
    try {
      await api.put(`/meetings/${meetingId}/agenda/${editForm.id}`, {
        title: editForm.title,
        description: editForm.description,
        category: editForm.category,
        proposal: editForm.proposal,
        responsible: editForm.responsible,
        duration_min: editForm.duration_min ? parseInt(editForm.duration_min, 10) : null,
      });
      toast.success("Saken er oppdatert");
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingEdit(false);
    }
  };

  const draftEditCase = async () => {
    if (!editForm.title.trim()) { toast.error("Skriv en sakstittel først"); return; }
    setEditDrafting(true);
    try {
      const { data } = await api.post(`/meetings/${meetingId}/draft-case`, { title: editForm.title, context: editForm.description });
      setEditForm((f) => ({ ...f, description: data.description || f.description, proposal: data.proposal || f.proposal }));
      toast.success("AI fylte ut saksfremlegg");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setEditDrafting(false);
    }
  };

  // Dra-og-slipp rekkefølge
  const [items, setItems] = useState(m.agenda || []);
  const itemsRef = useRef(m.agenda || []);
  useEffect(() => { setItems(m.agenda || []); itemsRef.current = m.agenda || []; }, [m.agenda]);
  const onReorder = (next) => { itemsRef.current = next; setItems(next); };
  const persistReorder = async () => {
    try {
      await api.put(`/meetings/${meetingId}/agenda/reorder`, { order: itemsRef.current.map((a) => a.id) });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setItems(m.agenda || []);
      itemsRef.current = m.agenda || [];
    }
  };

  // Slett sak
  const [deleteFor, setDeleteFor] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const confirmDelete = async () => {
    if (!deleteFor) return;
    setDeleting(true);
    try {
      await api.delete(`/meetings/${meetingId}/agenda/${deleteFor.id}`);
      toast.success("Saken er slettet");
      if (selectedId === deleteFor.id) setSelectedId(null);
      setDeleteFor(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  // Vedlegg per sak
  const [attachments, setAttachments] = useState([]);
  const [uploadingAtt, setUploadingAtt] = useState(false);
  const caseFileRef = useRef(null);
  const loadAttachments = () => api.get(`/meetings/${meetingId}/attachments`).then((r) => setAttachments(r.data || [])).catch(() => setAttachments([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAttachments(); }, [meetingId]);
  const onCaseFile = async (e, itemId) => {
    const file = e.target.files?.[0];
    if (!file || !itemId) return;
    setUploadingAtt(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/meetings/${meetingId}/attachments?agenda_item_id=${itemId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Vedlegg lagt til saken");
      loadAttachments();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploadingAtt(false);
      if (caseFileRef.current) caseFileRef.current.value = "";
    }
  };
  const removeCaseAtt = async (id) => {
    try {
      await api.delete(`/documents/${id}`);
      toast.success("Vedlegg fjernet");
      loadAttachments();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  // Dupliser sak
  const [copyToOpen, setCopyToOpen] = useState(false);
  const [copySource, setCopySource] = useState(null);
  const [otherMeetings, setOtherMeetings] = useState([]);
  const [copyTarget, setCopyTarget] = useState("");
  const [copying, setCopying] = useState(false);
  const duplicateHere = async (a) => {
    try {
      await api.post(`/meetings/${meetingId}/agenda/${a.id}/duplicate`);
      toast.success(`Kopi av «${a.title}» lagt til nederst i saklisten`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const openCopyTo = async (a) => {
    setCopySource(a);
    setCopyTarget("");
    setCopyToOpen(true);
    try {
      const { data } = await api.get(`/companies/${companyId}/meetings`);
      setOtherMeetings((data || []).filter((mm) => mm.id !== meetingId));
    } catch {
      setOtherMeetings([]);
    }
  };
  const confirmCopyTo = async () => {
    if (!copyTarget) { toast.error("Velg et møte"); return; }
    setCopying(true);
    try {
      const { data } = await api.post(`/meetings/${meetingId}/agenda/${copySource.id}/copy-to`, { target_meeting_id: copyTarget });
      toast.success(`Saken er kopiert til «${data.target_title}»`);
      setCopyToOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCopying(false);
    }
  };

  const openDecision = (a) => {
    setDecisionFor(a);
    setDecisionForm({ decision: a.decision || a.proposal || "", create_task: true, task_title: a.title || "", assignee_name: a.responsible || "", due_date: "" });
  };

  const recordDecision = async () => {
    if (!decisionForm.decision.trim()) { toast.error("Skriv inn beslutningen"); return; }
    setSavingDecision(true);
    try {
      await api.post(`/meetings/${meetingId}/agenda/${decisionFor.id}/decision`, decisionForm);
      toast.success(decisionForm.create_task ? "Beslutning registrert og oppgave opprettet" : "Beslutning registrert");
      setDecisionFor(null);
      load();
      loadTasks();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingDecision(false);
    }
  };

  const toggleReviewTask = async (t) => {
    const next = t.status === "ferdig" ? "apen" : "ferdig";
    await api.put(`/tasks/${t.id}`, { status: next });
    loadTasks();
  };

  const addAgenda = async () => {
    if (!agenda.title) return;
    try {
      await api.post(`/meetings/${meetingId}/agenda`, {
        ...agenda,
        duration_min: agenda.duration_min ? parseInt(agenda.duration_min, 10) : null,
      });
      setAgendaOpen(false);
      setAgenda({ title: "", description: "", category: "Sak", proposal: "", duration_min: "", responsible: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const addAgendaItem = async (item) => {
    const { meta, ...payload } = item;
    try {
      await api.post(`/meetings/${meetingId}/agenda`, payload);
      toast.success(`La til «${item.title}»`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const draftCase = async () => {
    if (!agenda.title) {
      toast.error("Skriv en sakstittel først");
      return;
    }
    setDrafting(true);
    try {
      const { data } = await api.post(`/meetings/${meetingId}/draft-case`, { title: agenda.title, context: agenda.description });
      setAgenda((a) => ({ ...a, description: data.description || a.description, proposal: data.proposal || a.proposal }));
      toast.success("AI fylte ut saksfremlegg");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDrafting(false);
    }
  };

  const suggestAgenda = async () => {
    setSuggLoading(true);
    setSuggestions([]);
    const seen = new Set();
    const pushLine = (line) => {
      const t = line.trim().replace(/^```(json)?/, "").replace(/```$/, "").trim();
      if (!t || !t.startsWith("{")) return;
      try {
        const o = JSON.parse(t);
        if (o && o.title && !seen.has(o.title)) {
          seen.add(o.title);
          setSuggestions((prev) => [...(prev || []), {
            title: String(o.title).slice(0, 200),
            category: String(o.category || "Sak").slice(0, 40),
            description: String(o.description || ""),
            proposal: String(o.proposal || ""),
            duration_min: Number.isInteger(o.duration_min) ? o.duration_min : null,
          }]);
        }
      } catch { /* ufullstendig eller ugyldig linje – hopp over */ }
    };
    try {
      const res = await fetch(`${API}/meetings/${meetingId}/suggest-agenda/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({}),
      });
      if (!res.ok || !res.body) throw new Error("stream failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let nl;
        while ((nl = buf.indexOf("\n")) !== -1) {
          pushLine(buf.slice(0, nl));
          buf = buf.slice(nl + 1);
        }
      }
      buf += decoder.decode();
      if (buf.trim()) pushLine(buf);
    } catch (e) {
      try {
        const { data } = await api.post(`/meetings/${meetingId}/suggest-agenda`, {});
        setSuggestions(data.suggestions || []);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail));
        setSuggestions([]);
      }
    } finally {
      setSuggLoading(false);
    }
  };

  const openSheet = () => {
    setSheetOpen(true);
    if (suggestions === null && !suggLoading) suggestAgenda();
  };

  const agendaItems = m.agenda || [];
  const sel = agendaItems.find((a) => a.id === selectedId) || agendaItems[0] || null;
  const totalMin = agendaItems.reduce((s, a) => s + (a.duration_min || 0), 0);

  const q = sheetSearch.trim().toLowerCase();
  const fastFiltered = FAST_SAKER.filter((f) => !q || f.title.toLowerCase().includes(q));
  const aiFiltered = (suggestions || []).filter((s) => !q || (s.title || "").toLowerCase().includes(q));

  return (
    <>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* Master */}
        <div className="lg:col-span-5 xl:col-span-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-headings text-lg font-bold tracking-tight">Saksliste</h3>
            <div className="flex items-center gap-1.5">
              <Button size="sm" className="h-8 gap-1.5" onClick={openSheet} data-testid="add-agenda-btn">
                <Plus className="h-4 w-4" /> Legg til sak
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="icon" variant="ghost" className="h-8 w-8" data-testid="agenda-more-btn"><MoreHorizontal className="h-4 w-4" /></Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onClick={openSheet} data-testid="ai-suggest-agenda-btn"><Sparkles className="mr-2 h-4 w-4 text-primary" /> AI-forslag til saksliste</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setAgendaOpen(true)} data-testid="add-custom-agenda"><Plus className="mr-2 h-4 w-4" /> Legg til egendefinert sak</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          <Reorder.Group axis="y" values={items} onReorder={onReorder} className="list-none space-y-2.5 p-0" data-testid="agenda-master-list">
            {items.map((a, idx) => (
              <ReorderCard
                key={a.id}
                a={a}
                idx={idx}
                selected={sel?.id === a.id}
                onClick={() => { setSelectedId(a.id); setCaseMode("innkalling"); }}
                onDragEnd={persistReorder}
              />
            ))}
          </Reorder.Group>
          {items.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              Ingen saker lagt til ennå. Bruk «Legg til sak» for å komme raskt i gang.
            </div>
          ) : (
            <p className="mt-2 flex items-center gap-1.5 text-[11px] text-muted-foreground/70" data-testid="reorder-hint">
              <GripVertical className="h-3 w-3" /> Dra i håndtaket for å endre rekkefølgen på sakene.
            </p>
          )}

          {agendaItems.length > 0 ? (
            <div className="mt-3 flex items-center gap-2 border-t border-border/60 pt-3 text-xs text-muted-foreground" data-testid="agenda-total-duration">
              <Clock className="h-3.5 w-3.5" /> Estimert varighet: <span className="font-semibold text-foreground">{durLabel(totalMin)}</span>
            </div>
          ) : null}
        </div>

        {/* Detail */}
        <div className="lg:col-span-7 xl:col-span-8">
          {!sel ? (
            <div className="grid h-full min-h-[280px] place-items-center rounded-2xl border border-dashed border-border text-sm text-muted-foreground">
              Velg en sak fra listen for å se detaljene.
            </div>
          ) : (
            <div className="flex gap-4">
              <motion.div
                key={sel.id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                className="min-w-0 flex-1 rounded-2xl border border-border/60 bg-card p-5 shadow-sm sm:p-7"
                data-testid="case-detail"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                    <span>Sak {sel.order}</span>
                    <ChevronRight className="h-3 w-3" />
                    <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] uppercase tracking-wider">{sel.category}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" className="h-7 gap-1.5 px-2 text-xs" onClick={() => openEdit(sel)} data-testid="edit-case-btn">
                      <Pencil className="h-3.5 w-3.5" /> Rediger sak
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="ghost" className="h-7 gap-1.5 px-2 text-xs" data-testid="duplicate-case-btn">
                          <Copy className="h-3.5 w-3.5" /> Dupliser
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-52">
                        <DropdownMenuItem onClick={() => duplicateHere(sel)} data-testid="duplicate-here"><Copy className="mr-2 h-4 w-4" /> Dupliser her</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openCopyTo(sel)} data-testid="copy-to-meeting"><ChevronRight className="mr-2 h-4 w-4" /> Kopier til møte …</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <Button size="sm" variant="ghost" className="h-7 gap-1.5 px-2 text-xs text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => setDeleteFor(sel)} data-testid="delete-case-btn">
                      <Trash2 className="h-3.5 w-3.5" /> Slett
                    </Button>
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                  <ToggleGroup type="single" value={caseMode} onValueChange={(v) => v && setCaseMode(v)} className="rounded-lg bg-muted p-1" data-testid="case-mode-toggle">
                    <ToggleGroupItem value="innkalling" data-testid="case-mode-innkalling" className="h-7 gap-1.5 px-3 text-xs data-[state=on]:bg-background data-[state=on]:shadow">
                      <Mail className="h-3.5 w-3.5" /> Innkalling
                    </ToggleGroupItem>
                    <ToggleGroupItem value="referat" data-testid="case-mode-referat" className="h-7 gap-1.5 px-3 text-xs data-[state=on]:bg-background data-[state=on]:shadow">
                      <ListChecks className="h-3.5 w-3.5" /> Referat
                    </ToggleGroupItem>
                  </ToggleGroup>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    {sel.responsible ? <span className="flex items-center gap-1"><User className="h-3.5 w-3.5" /> {sel.responsible}</span> : null}
                    {sel.duration_min ? <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {sel.duration_min} min</span> : null}
                  </div>
                </div>

                <h2 className="mt-4 font-headings text-xl font-extrabold tracking-tight sm:text-2xl">{sel.title}</h2>

                {caseMode === "innkalling" ? (
                  <div className="mt-4 space-y-4">
                    {sel.kind === "task_review" ? (
                      <div className="space-y-1.5" data-testid={`task-review-${sel.id}`}>
                        <p className="text-sm font-medium">Oppgaver fra forrige styremøte</p>
                        {companyTasks.filter((t) => (sel.review_task_ids || []).includes(t.id)).map((t) => (
                          <label key={t.id} className="flex cursor-pointer items-center gap-2.5 rounded-lg border border-border/60 bg-muted/30 px-3 py-2 text-sm">
                            <Checkbox checked={t.status === "ferdig"} onCheckedChange={() => toggleReviewTask(t)} data-testid={`review-task-${t.id}`} />
                            <span className={`flex-1 ${t.status === "ferdig" ? "text-muted-foreground line-through" : ""}`}>{t.title}{t.assignee_name ? ` · ${t.assignee_name}` : ""}</span>
                            <StatusBadge status={t.status} />
                          </label>
                        ))}
                        {companyTasks.filter((t) => (sel.review_task_ids || []).includes(t.id)).length === 0 ? <p className="text-xs text-muted-foreground">Ingen oppgaver fra forrige styremøte.</p> : null}
                      </div>
                    ) : sel.description ? (
                      <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">{sel.description}</p>
                    ) : (
                      <button
                        type="button"
                        onClick={() => openEdit(sel)}
                        data-testid="add-description-placeholder"
                        className="text-left text-sm italic text-muted-foreground underline decoration-dotted underline-offset-4 transition-colors hover:text-foreground"
                      >
                        Legg til beskrivelse / saksfremlegg …
                      </button>
                    )}

                    {sel.proposal ? (
                      <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 px-4 py-3 text-sm">
                        <p className="mb-1 text-xs font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400">{isGf ? "Forslag til vedtak" : "Forslag til beslutning"}</p>
                        {sel.proposal}
                      </div>
                    ) : null}

                    {isGf ? (
                      <GfVoting meetingId={meetingId} item={sel} gf={gf} onChange={load} />
                    ) : sel.kind !== "task_review" ? (
                      <LivePoll meetingId={meetingId} item={sel} live={live} refreshLive={refreshLive} load={load} />
                    ) : null}
                  </div>
                ) : (
                  <div className="mt-4 space-y-4">
                    {sel.decision ? (
                      <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 px-4 py-3 text-sm" data-testid="case-decision">
                        <p className="mb-1 text-xs font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-400">{isGf ? "Vedtak" : "Beslutning"}</p>
                        <p className="whitespace-pre-wrap">{sel.decision}</p>
                      </div>
                    ) : (
                      <p className="text-sm italic text-muted-foreground">Ingen {isGf ? "vedtak" : "beslutning"} registrert på denne saken ennå.</p>
                    )}
                    {!isGf && sel.kind !== "task_review" ? (
                      <Button size="sm" variant="outline" className="gap-2" onClick={() => openDecision(sel)} data-testid={`record-decision-${sel.id}`}>
                        <Gavel className="h-4 w-4" /> {sel.decision ? "Endre beslutning" : "Registrer beslutning"}
                      </Button>
                    ) : null}
                    {isGf ? (
                      <GfVoting meetingId={meetingId} item={sel} gf={gf} onChange={load} />
                    ) : sel.kind !== "task_review" ? (
                      <LivePoll meetingId={meetingId} item={sel} live={live} refreshLive={refreshLive} load={load} />
                    ) : null}
                  </div>
                )}

                {/* Vedlegg til denne saken */}
                <div className="mt-6 border-t border-border/60 pt-4" data-testid="case-attachments">
                  <div className="mb-2 flex items-center justify-between">
                    <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      <Paperclip className="h-3.5 w-3.5 text-primary" /> Vedlegg til saken
                    </p>
                    <input ref={caseFileRef} type="file" className="hidden" onChange={(e) => onCaseFile(e, sel.id)} data-testid="case-attachment-input" />
                    <Button size="sm" variant="ghost" className="h-7 gap-1.5 text-xs" disabled={uploadingAtt} onClick={() => caseFileRef.current?.click()} data-testid="case-attachment-upload-btn">
                      {uploadingAtt ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Last opp
                    </Button>
                  </div>
                  {attachments.filter((d) => d.agenda_item_id === sel.id).length === 0 ? (
                    <p className="text-xs text-muted-foreground" data-testid="case-attachments-empty">Ingen dokumenter er knyttet til denne saken ennå.</p>
                  ) : (
                    <div className="space-y-1.5" data-testid="case-attachments-list">
                      {attachments.filter((d) => d.agenda_item_id === sel.id).map((d) => (
                        <div key={d.id} className="flex items-center gap-2.5 rounded-lg border border-border/60 bg-muted/30 px-3 py-2" data-testid={`case-attachment-${d.id}`}>
                          <FileText className="h-4 w-4 shrink-0 text-primary" />
                          <span className="min-w-0 flex-1 truncate text-sm">{d.name}</span>
                          <button className="grid h-7 w-7 place-items-center rounded-md text-muted-foreground transition-colors hover:text-primary" title="Last ned" data-testid={`case-attachment-download-${d.id}`} onClick={() => openSigned(`/documents/${d.id}/download-link`).catch(() => toast.error("Kunne ikke åpne dokumentet"))}>
                            <Download className="h-3.5 w-3.5" />
                          </button>
                          <button className="grid h-7 w-7 place-items-center rounded-md text-muted-foreground transition-colors hover:text-destructive" title="Fjern" data-testid={`case-attachment-delete-${d.id}`} onClick={() => removeCaseAtt(d.id)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <p className="mt-6 border-t border-border/60 pt-3 text-[11px] text-muted-foreground">Sist endret {formatDateTime(m.updated_at || m.date)}</p>
              </motion.div>

              {/* Action rail */}
              <TooltipProvider delayDuration={200}>
                <div className="hidden flex-col gap-1.5 lg:flex" data-testid="case-action-rail">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button size="icon" variant="ghost" className="h-9 w-9" data-testid="rail-edit" onClick={() => openEdit(sel)}><Pencil className="h-4 w-4" /></Button>
                    </TooltipTrigger>
                    <TooltipContent side="left">Rediger sak</TooltipContent>
                  </Tooltip>
                  {!isGf && sel.kind !== "task_review" ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button size="icon" variant="ghost" className="h-9 w-9" data-testid="rail-decision" onClick={() => openDecision(sel)}><Gavel className="h-4 w-4" /></Button>
                      </TooltipTrigger>
                      <TooltipContent side="left">Registrer beslutning</TooltipContent>
                    </Tooltip>
                  ) : null}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button asChild size="icon" variant="ghost" className="h-9 w-9" data-testid="rail-tasks">
                        <Link to={`/selskap/${companyId}/oppgaver`}><ListChecks className="h-4 w-4" /></Link>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="left">Oppgaver</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button asChild size="icon" variant="ghost" className="h-9 w-9" data-testid="rail-documents">
                        <Link to={`/selskap/${companyId}/dokumenter`}><Paperclip className="h-4 w-4" /></Link>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="left">Dokumenter</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button size="icon" variant="ghost" className="h-9 w-9" data-testid="rail-add" onClick={openSheet}><Sparkles className="h-4 w-4 text-primary" /></Button>
                    </TooltipTrigger>
                    <TooltipContent side="left">AI-forslag til saksliste</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button size="icon" variant="ghost" className="h-9 w-9 text-muted-foreground hover:bg-destructive/10 hover:text-destructive" data-testid="rail-delete" onClick={() => setDeleteFor(sel)}><Trash2 className="h-4 w-4" /></Button>
                    </TooltipTrigger>
                    <TooltipContent side="left">Slett sak</TooltipContent>
                  </Tooltip>
                </div>
              </TooltipProvider>
            </div>
          )}
        </div>
      </div>

      {/* Manual add-case dialog */}
      <Dialog open={agendaOpen} onOpenChange={setAgendaOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ny sak</DialogTitle>
            <DialogDescription>Legg til en sak. La gjerne AI fylle ut saksfremlegg og forslag til vedtak.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Tittel</Label><Input data-testid="agenda-title-input" className="mt-1.5" value={agenda.title} onChange={(e) => setAgenda({ ...agenda, title: e.target.value })} /></div>
            <div className="flex justify-end">
              <Button variant="ghost" size="sm" className="gap-2 text-primary" onClick={draftCase} disabled={drafting} data-testid="ai-draft-case-btn">
                {drafting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Foreslå med AI
              </Button>
            </div>
            <div><Label>Saksfremlegg</Label><Textarea className="mt-1.5" rows={3} value={agenda.description} onChange={(e) => setAgenda({ ...agenda, description: e.target.value })} /></div>
            <div><Label>Forslag til vedtak</Label><Textarea className="mt-1.5" rows={2} value={agenda.proposal} onChange={(e) => setAgenda({ ...agenda, proposal: e.target.value })} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Kategori</Label><Input className="mt-1.5" value={agenda.category} onChange={(e) => setAgenda({ ...agenda, category: e.target.value })} /></div>
              <div><Label>Ansvarlig</Label>
                <Select value={agenda.responsible ? agenda.responsible : "__none__"} onValueChange={(v) => setAgenda({ ...agenda, responsible: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="agenda-responsible-select" className="mt-1.5"><SelectValue placeholder="Velg" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Ingen</SelectItem>
                    {agenda.responsible && !assignable.some((x) => x.name === agenda.responsible) ? <SelectItem value={agenda.responsible}>{agenda.responsible}</SelectItem> : null}
                    {assignable.map((x) => <SelectItem key={x.user_id || x.name} value={x.name}>{x.name}{x.role ? ` · ${x.role}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Varighet (min)</Label><Input type="number" className="mt-1.5" value={agenda.duration_min} onChange={(e) => setAgenda({ ...agenda, duration_min: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAgendaOpen(false)}>Avbryt</Button>
            <Button onClick={addAgenda} data-testid="save-agenda-btn">Legg til</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rediger sak-dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="edit-case-dialog">
          <DialogHeader>
            <DialogTitle>Rediger sak</DialogTitle>
            <DialogDescription>Oppdater tittel, saksfremlegg og forslag til vedtak. La gjerne AI hjelpe deg.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Tittel</Label><Input data-testid="edit-title-input" className="mt-1.5" value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} /></div>
            <div className="flex justify-end">
              <Button variant="ghost" size="sm" className="gap-2 text-primary" onClick={draftEditCase} disabled={editDrafting} data-testid="edit-ai-draft-btn">
                {editDrafting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Foreslå med AI
              </Button>
            </div>
            <div><Label>Saksfremlegg</Label><Textarea data-testid="edit-description-input" className="mt-1.5" rows={4} value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} /></div>
            <div><Label>Forslag til vedtak</Label><Textarea data-testid="edit-proposal-input" className="mt-1.5" rows={2} value={editForm.proposal} onChange={(e) => setEditForm({ ...editForm, proposal: e.target.value })} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Kategori</Label><Input data-testid="edit-category-input" className="mt-1.5" value={editForm.category} onChange={(e) => setEditForm({ ...editForm, category: e.target.value })} /></div>
              <div><Label>Ansvarlig</Label>
                <Select value={editForm.responsible ? editForm.responsible : "__none__"} onValueChange={(v) => setEditForm({ ...editForm, responsible: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="edit-responsible-select" className="mt-1.5"><SelectValue placeholder="Velg" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Ingen</SelectItem>
                    {editForm.responsible && !assignable.some((x) => x.name === editForm.responsible) ? <SelectItem value={editForm.responsible}>{editForm.responsible}</SelectItem> : null}
                    {assignable.map((x) => <SelectItem key={x.user_id || x.name} value={x.name}>{x.name}{x.role ? ` · ${x.role}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Varighet (min)</Label><Input type="number" data-testid="edit-duration-input" className="mt-1.5" value={editForm.duration_min} onChange={(e) => setEditForm({ ...editForm, duration_min: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditOpen(false)}>Avbryt</Button>
            <Button onClick={saveEdit} disabled={savingEdit} data-testid="save-edit-btn">{savingEdit ? "Lagrer …" : "Lagre endringer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Slett sak-bekreftelse */}
      <AlertDialog open={!!deleteFor} onOpenChange={(o) => !o && setDeleteFor(null)}>
        <AlertDialogContent data-testid="delete-case-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Slette sak?</AlertDialogTitle>
            <AlertDialogDescription>
              Er du sikker på at du vil fjerne «{deleteFor?.title}» fra saklisten? Dette kan ikke angres.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} disabled={deleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="delete-confirm-btn">
              {deleting ? "Sletter …" : "Slett sak"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Kopier sak til et annet møte */}
      <Dialog open={copyToOpen} onOpenChange={setCopyToOpen}>
        <DialogContent data-testid="copy-to-dialog">
          <DialogHeader>
            <DialogTitle>Kopier sak til et annet møte</DialogTitle>
            <DialogDescription>{copySource?.title}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label>Velg møte</Label>
            {otherMeetings.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="copy-to-empty">Ingen andre møter i dette selskapet ennå. Opprett et nytt møte først.</p>
            ) : (
              <Select value={copyTarget} onValueChange={setCopyTarget}>
                <SelectTrigger data-testid="copy-to-select"><SelectValue placeholder="Velg møte …" /></SelectTrigger>
                <SelectContent>
                  {otherMeetings.map((mm) => (
                    <SelectItem key={mm.id} value={mm.id}>{mm.title} · {formatDateTime(mm.date)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCopyToOpen(false)}>Avbryt</Button>
            <Button onClick={confirmCopyTo} disabled={copying || !copyTarget} data-testid="copy-to-confirm">{copying ? "Kopierer …" : "Kopier sak"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Decision dialog */}
      <Dialog open={!!decisionFor} onOpenChange={(o) => !o && setDecisionFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registrer beslutning</DialogTitle>
            <DialogDescription>{decisionFor?.title}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Beslutning</Label><Textarea data-testid="decision-text" className="mt-1.5" rows={3} value={decisionForm.decision} onChange={(e) => setDecisionForm({ ...decisionForm, decision: e.target.value })} /></div>
            <label className="flex cursor-pointer items-start gap-3">
              <Checkbox data-testid="decision-create-task" checked={decisionForm.create_task} onCheckedChange={(v) => setDecisionForm({ ...decisionForm, create_task: !!v })} className="mt-0.5" />
              <span><span className="text-sm font-medium">Opprett oppfølgingsoppgave</span><span className="block text-xs text-muted-foreground">Lager en oppgave med ansvarlig og frist, knyttet til dette møtet.</span></span>
            </label>
            {decisionForm.create_task ? (
              <div className="space-y-3 rounded-lg border border-border/60 bg-muted/30 p-3">
                <div><Label className="text-xs">Oppgavetittel</Label><Input data-testid="decision-task-title" className="mt-1.5" value={decisionForm.task_title} onChange={(e) => setDecisionForm({ ...decisionForm, task_title: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Ansvarlig</Label>
                    <Select value={decisionForm.assignee_name ? decisionForm.assignee_name : "__none__"} onValueChange={(v) => setDecisionForm({ ...decisionForm, assignee_name: v === "__none__" ? "" : v })}>
                      <SelectTrigger data-testid="decision-assignee" className="mt-1.5"><SelectValue placeholder="Velg" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Ingen</SelectItem>
                        {assignable.map((x) => <SelectItem key={x.user_id || x.name} value={x.name}>{x.name}{x.role ? ` · ${x.role}` : ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Frist</Label><Input type="date" data-testid="decision-due" className="mt-1.5" value={decisionForm.due_date} onChange={(e) => setDecisionForm({ ...decisionForm, due_date: e.target.value })} /></div>
                </div>
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDecisionFor(null)}>Avbryt</Button>
            <Button onClick={recordDecision} disabled={savingDecision} data-testid="save-decision-btn">{savingDecision ? "Lagrer …" : "Lagre beslutning"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Legg til sak: slide-over */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="flex w-full flex-col gap-0 p-0 sm:max-w-md" data-testid="add-agenda-sheet">
          <SheetHeader className="border-b border-border p-5 text-left">
            <SheetTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> Legg til sak</SheetTitle>
            <SheetDescription>Velg en fast sak eller et AI-forslag – eller lag en egendefinert sak.</SheetDescription>
            <div className="relative mt-2">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="sheet-search" placeholder="Søk i forslag …" className="pl-9" value={sheetSearch} onChange={(e) => setSheetSearch(e.target.value)} />
            </div>
          </SheetHeader>

          <div className="flex-1 space-y-6 overflow-y-auto p-5">
            {/* Fast saker */}
            <div>
              <p className="mb-2 text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Faste saker</p>
              <div className="space-y-2">
                {fastFiltered.map((f) => (
                  <div key={f.title} data-testid={`fast-sak-${f.title}`} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold">{f.title}</p>
                      <p className="text-xs text-muted-foreground">{f.meta}{f.duration_min ? ` · ${f.duration_min} min` : ""}</p>
                    </div>
                    <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => addAgendaItem(f)} data-testid={`add-fast-${f.title}`}><Plus className="h-4 w-4" /></Button>
                  </div>
                ))}
                {fastFiltered.length === 0 ? <p className="text-xs text-muted-foreground">Ingen faste saker treffer søket.</p> : null}
              </div>
            </div>

            {/* AI forslag */}
            <div>
              <div className="mb-2 flex items-center justify-between">
                <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-muted-foreground"><Sparkles className="h-3.5 w-3.5 text-primary" /> AI · forslag</p>
                <Button size="sm" variant="ghost" className="h-7 gap-1.5 text-xs" onClick={suggestAgenda} disabled={suggLoading} data-testid="sheet-refresh-ai">
                  {suggLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Hent forslag
                </Button>
              </div>
              {suggLoading && (!suggestions || suggestions.length === 0) ? (
                <div className="flex flex-col items-center justify-center gap-2 py-8 text-center text-sm text-muted-foreground" data-testid="ai-suggestions-initial">
                  <Loader2 className="h-5 w-5 animate-spin text-primary" />
                  <p className="font-medium text-foreground">Styresekretæren skriver forslag …</p>
                  <p className="text-xs">Forslagene dukker opp fortløpende etter hvert som AI-en skriver.</p>
                </div>
              ) : (
                <div className="space-y-2" data-testid="ai-suggestions-list">
                  {aiFiltered.map((sg, i) => (
                    <div key={i} data-testid={`ai-suggestion-${i}`} className="flex items-start gap-3 rounded-xl border border-primary/20 bg-primary/[0.03] p-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold">{sg.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {sg.category}{sg.duration_min ? ` · ${sg.duration_min} min` : ""}
                        </p>
                        {sg.proposal ? <p className="mt-1 text-xs"><span className="font-medium text-amber-700 dark:text-amber-400">Forslag: </span>{sg.proposal}</p> : null}
                      </div>
                      <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => addAgendaItem(sg)} data-testid={`add-suggestion-${i}`}><Plus className="h-4 w-4" /></Button>
                    </div>
                  ))}
                  {suggLoading ? (
                    <div className="flex items-center gap-2 rounded-xl border border-dashed border-primary/20 px-3 py-2.5 text-xs text-muted-foreground" data-testid="ai-suggestions-streaming">
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" /> Styresekretæren skriver flere forslag …
                    </div>
                  ) : null}
                  {!suggLoading && suggestions && aiFiltered.length === 0 ? <p className="text-xs text-muted-foreground">Ingen AI-forslag {q ? "treffer søket" : "akkurat nå"}.</p> : null}
                </div>
              )}
            </div>
          </div>

          <div className="border-t border-border p-4">
            <Button variant="outline" className="w-full gap-2" onClick={() => { setSheetOpen(false); setAgendaOpen(true); }} data-testid="sheet-custom-btn">
              <Plus className="h-4 w-4" /> Legg til egendefinert sak
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
